import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import CloseCommentPanel from 'actions/task/close_comment_panel';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import connect from 'components/lib/connect';
import { useMobilePanel } from 'components/mobile/mobile_context';

const PANEL_OPTIONS = [
  { key: null, label: 'Conversation' },
  { key: 'profile', label: 'Profile' },
  { key: 'answers', label: 'Answers' },
  { key: 'team-assist', label: 'Team Assist' },
  { key: 'tasks', label: 'Tasks' },
];

export function MobilePanelMenu({ selectedTaskId }) {
  const { mobilePanel, setMobilePanel } = useMobilePanel();
  const executeAction = useExecuteAction();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const prevSelectedTaskId = useRef(selectedTaskId);
  const prevMobilePanel = useRef(mobilePanel);
  const options = useMemo(
    () => (selectedTaskId ? PANEL_OPTIONS : PANEL_OPTIONS.filter(o => o.key !== 'tasks')),
    [selectedTaskId]
  );

  // When a task is selected (or a different task is picked) from the feed,
  // surface the Tasks panel automatically — otherwise the agent would tap a
  // task and see no change.
  useEffect(() => {
    if (selectedTaskId && selectedTaskId !== prevSelectedTaskId.current) {
      setMobilePanel('tasks');
    }
    prevSelectedTaskId.current = selectedTaskId;
  }, [selectedTaskId, setMobilePanel]);

  // Leaving the Tasks panel (via the dropdown or the customer-page back
  // button) clears the selected task. Without this, re-tapping the same task
  // item in the feed wouldn't be a state transition and the panel wouldn't
  // re-open.
  useEffect(() => {
    if (prevMobilePanel.current === 'tasks' && mobilePanel !== 'tasks' && selectedTaskId) {
      executeAction(CloseCommentPanel);
    }
    prevMobilePanel.current = mobilePanel;
  }, [mobilePanel, selectedTaskId, executeAction]);

  const toggle = useCallback(() => setIsOpen(prev => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  const selectPanel = useCallback(
    key => {
      setMobilePanel(key);
      close();
    },
    [setMobilePanel, close]
  );

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(evt) {
      if (menuRef.current && !menuRef.current.contains(evt.target)) {
        close();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, close]);

  const activeLabel = options.find(o => o.key === mobilePanel)?.label || 'Conversation';

  return (
    <MenuWrapper ref={menuRef}>
      <MenuButton
        aria-expanded={isOpen}
        aria-haspopup="menu"
        data-aid="mobilePanelMenuButton"
        onClick={toggle}
        type="button"
      >
        <ActiveLabel>{activeLabel}</ActiveLabel>
        <Chevron $isOpen={isOpen}>&#9662;</Chevron>
      </MenuButton>
      {isOpen && (
        <Dropdown data-aid="mobilePanelMenu" role="menu">
          {options.map(option => (
            <DropdownItem
              $isActive={option.key === mobilePanel}
              aria-checked={option.key === mobilePanel}
              data-aid={`mobilePanelMenu-${option.key || 'conversation'}`}
              key={option.key || 'conversation'}
              onClick={() => selectPanel(option.key)}
              role="menuitemradio"
              type="button"
            >
              {option.label}
              {option.key === mobilePanel && <Check>&#10003;</Check>}
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </MenuWrapper>
  );
}

MobilePanelMenu.propTypes = {
  selectedTaskId: PropTypes.string,
};

function mapStateToProps({ getProvider }) {
  return {
    selectedTaskId: getProvider('commentPanel').get().selectedItemId || null,
  };
}

export default connect(mapStateToProps)(MobilePanelMenu);

const MenuWrapper = styled.div`
  position: relative;
  margin-left: auto;
`;

const MenuButton = styled.button`
  align-items: center;
  background: none;
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: ${p => p.theme.borderRadius.default};
  color: ${p => p.theme.colors.gray800};
  cursor: pointer;
  display: flex;
  font-size: ${p => p.theme.fontSize.base};
  gap: ${p => p.theme.spacing.small};
  height: 32px;
  padding: 0 ${p => p.theme.spacing.medium};

  &:active {
    background-color: ${p => p.theme.colors.gray100};
  }
`;

const ActiveLabel = styled.span`
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled.span`
  font-size: ${p => p.theme.fontSize.small};
  transform: ${p => (p.$isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.15s ease;
`;

const Dropdown = styled.div`
  background: ${p => p.theme.colors.white};
  border-radius: ${p => p.theme.borderRadius.large};
  box-shadow: ${p => p.theme.boxShadow.large};
  min-width: 160px;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 20;
`;

const DropdownItem = styled.button`
  align-items: center;
  background-color: ${p => (p.$isActive ? p.theme.colors.green100 : 'transparent')};
  border: none;
  cursor: pointer;
  display: flex;
  font: inherit;
  font-size: 14px;
  justify-content: space-between;
  padding: ${p => p.theme.spacing.medium} 14px;
  text-align: left;
  width: 100%;

  &:active {
    background-color: ${p => p.theme.colors.green100};
  }
`;

const Check = styled.span`
  color: ${p => p.theme.colors.green500};
  font-size: 14px;
`;
